import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Autocomplete,
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import useImageSelect from '../../hooks/useImageSelect';
import { useLocalStorage } from '../../hooks/useLocalStorage';

/** Keep in sync with OnboardingSlide.*MaxLength on the API. */
const TITLE_MAX = 80;
const BODY_MAX = 400;
const CTA_MAX = 40;

const resolveImageUrl = (image: any): string | null => {
  if (!image) return null;
  return (
    image.thumbnailUrl ||
    image.ThumbnailUrl ||
    image.url1_1 ||
    image.Url1_1 ||
    image.originalUrl ||
    image.OriginalUrl ||
    null
  );
};

/**
 * Phone-framed preview approximating Flutter CustomOnboardingPage
 * (Kihagyás → kép → cím → szöveg → dots → CTA).
 */
const MobileSlidePreview = ({
  title,
  body,
  ctaLabel,
  image,
}: {
  title: string;
  body: string;
  ctaLabel: string;
  image: any;
}) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const imageUrl = resolveImageUrl(image);
  const buttonLabel = ctaLabel?.trim() || 'Tovább';

  return (
    <Box
      sx={{
        position: { md: 'sticky' },
        top: { md: 16 },
      }}
    >
      <Typography variant="subtitle2" gutterBottom>
        Mobil előnézet
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 2 }}
      >
        Nagyjából a Custom onboarding layout — tipográfia / színek tenant
        témánként eltérhetnek.
      </Typography>

      {/* Device chrome */}
      <Box
        sx={{
          width: 300,
          mx: 'auto',
          borderRadius: '36px',
          border: '10px solid #1a1a1a',
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
          bgcolor: '#1a1a1a',
          overflow: 'hidden',
        }}
      >
        {/* Notch */}
        <Box
          sx={{
            height: 22,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-end',
            bgcolor: '#1a1a1a',
          }}
        >
          <Box
            sx={{
              width: 96,
              height: 10,
              mb: 0.5,
              borderRadius: '0 0 10px 10px',
              bgcolor: '#0d0d0d',
            }}
          />
        </Box>

        {/* Screen — MainContainer-like soft gradient */}
        <Box
          sx={{
            height: 560,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
            background: `linear-gradient(
              180deg,
              rgba(0,0,0,0.18) 0%,
              rgba(255,255,255,0.72) 5%,
              #fff 40%,
              #fff 100%
            )`,
            bgcolor: '#fff',
          }}
        >
          {/* Kihagyás — Align right TextButton */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: 1,
              pt: 0.5,
              flexShrink: 0,
            }}
          >
            <Typography
              sx={{
                color: primary,
                fontSize: 15,
                fontWeight: 500,
                px: 1.5,
                py: 0.75,
                userSelect: 'none',
              }}
            >
              Kihagyás
            </Typography>
          </Box>

          {/* Page content — padding horizontal 24 */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              px: '24px',
            }}
          >
            {/* Image flex 5, radius 16 */}
            <Box
              sx={{
                flex: 5,
                minHeight: 0,
                borderRadius: '16px',
                overflow: 'hidden',
                bgcolor: `${primary}14`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt=""
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              ) : (
                <ImageOutlinedIcon sx={{ fontSize: 56, color: primary, opacity: 0.7 }} />
              )}
            </Box>

            <Box sx={{ height: 24, flexShrink: 0 }} />

            {/* Title + body flex 4 */}
            <Box
              sx={{
                flex: 4,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                textAlign: 'center',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'inherit',
                  fontSize: 22,
                  fontWeight: 700,
                  color: primary,
                  lineHeight: 1.25,
                  flexShrink: 0,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {title?.trim() || 'Cím'}
              </Typography>
              <Box sx={{ height: 12, flexShrink: 0 }} />
              <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <Typography
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 15,
                    lineHeight: 1.45,
                    color: 'rgba(0,0,0,0.87)',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {body?.trim() || 'Szöveg…'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Page dots */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              py: 1,
              flexShrink: 0,
            }}
          >
            {[0, 1, 2].map((i) => (
              <Box
                key={i}
                sx={{
                  width: i === 0 ? 18 : 8,
                  height: 8,
                  borderRadius: 8,
                  bgcolor: i === 0 ? primary : `${primary}40`,
                }}
              />
            ))}
          </Box>

          {/* CTA — full width ElevatedButton */}
          <Box sx={{ px: '24px', pt: '12px', pb: '24px', flexShrink: 0 }}>
            <Box
              sx={{
                width: '100%',
                py: '14px',
                borderRadius: '4px',
                bgcolor: primary,
                color: '#fff',
                textAlign: 'center',
                fontSize: 17,
                fontWeight: 500,
                fontFamily: 'inherit',
                userSelect: 'none',
              }}
            >
              {buttonLabel}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const normalizeSlideValues = (values: any) => {
  const imageIdRaw = values.imageId ?? values.image?.id;
  const imageIdNum =
    imageIdRaw === '' || imageIdRaw == null || imageIdRaw === undefined
      ? null
      : Number(imageIdRaw);
  const orderNum = Number(values.order);

  return {
    ...values,
    imageId:
      Number.isFinite(imageIdNum as number) && (imageIdNum as number) > 0
        ? imageIdNum
        : null,
    order: Number.isFinite(orderNum) ? orderNum : 1,
    title: values.title ?? '',
    body: values.body ?? '',
    ctaLabel: values.ctaLabel ?? '',
  };
};

const OnboardingSlideForm = (props: any) => {
  const {
    entity,
    onSubmit,
    errors,
    handleCancel,
    readonly = false,
    publishers,
  } = props;

  const {
    ImageSelectComponent,
    uploadImage,
    uploading,
    pendingImageUpload,
  } = useImageSelect();

  const handleUploaded = (image: any, setFieldValue: any) => {
    setFieldValue('imageId', image.id);
    setFieldValue('image', image);
  };

  const publisherLocalStorage = useLocalStorage(
    'OnboardingSlideForm_publisherId'
  );

  const isSmallScreen = useMediaQuery((theme: any) =>
    theme.breakpoints.down('sm')
  );

  const [publisherId, setPublisherId] = useState<any>(
    publisherLocalStorage.value
  );

  useEffect(() => {
    if (!publisherId) {
      setPublisherId(publisherLocalStorage.value);
    }
  }, [publisherLocalStorage.value]);

  return (
    <Formik
      initialValues={{ ...entity, publisherId: publisherId ?? null }}
      enableReinitialize={true}
      validate={(values) => {
        const validationErrors: any = {};
        if (!values.title?.trim()) {
          validationErrors.title = 'A cím megadása kötelező';
        } else if ((values.title ?? '').length > TITLE_MAX) {
          validationErrors.title = `A cím legfeljebb ${TITLE_MAX} karakter lehet`;
        }
        if ((values.body ?? '').length > BODY_MAX) {
          validationErrors.body = `A szöveg legfeljebb ${BODY_MAX} karakter lehet`;
        }
        if ((values.ctaLabel ?? '').length > CTA_MAX) {
          validationErrors.ctaLabel = `A gomb felirata legfeljebb ${CTA_MAX} karakter lehet`;
        }
        if (pendingImageUpload && !(values.publisherId ?? publisherId)) {
          validationErrors.publisherId =
            'Új kép feltöltéséhez válassz publikálót';
        }
        return validationErrors;
      }}
      onSubmit={(values, { setSubmitting }) => {
        const selectedPublisherId = values.publisherId ?? publisherId;

        let newValues = normalizeSlideValues(values);

        const submit = () => {
          newValues = normalizeSlideValues({
            ...values,
            ...newValues,
            imageId: newValues.imageId ?? values.imageId ?? values.image?.id,
          });
          const { publisherId: _omit, ...payload } = newValues;
          onSubmit(payload, setSubmitting);
        };

        if (pendingImageUpload) {
          if (!selectedPublisherId) {
            setSubmitting(false);
            return;
          }
          uploadImage(
            (image: any) => {
              newValues = normalizeSlideValues({
                ...values,
                image,
                imageId: image?.id,
              });
            },
            'kép',
            selectedPublisherId,
            '-1'
          )?.then(submit);
          return;
        }

        submit();
      }}
    >
      {({
        isSubmitting,
        values,
        touched,
        setFieldValue,
        handleChange,
        errors: validationErrors,
        submitCount,
      }) => {
        const showError = (field: string) =>
          (!!touched[field] || submitCount > 0) && !!validationErrors[field];
        const titleLen = (values.title ?? '').length;
        const bodyLen = (values.body ?? '').length;

        return (
          <Form noValidate>
            <Paper>
              <Grid container spacing={3} sx={{ p: 5 }}>
                <Grid size={12}>
                  <h2>
                    Bevezető slide{' '}
                    {values.id > 0 ? 'szerkesztés' : 'létrehozás'}
                  </h2>
                </Grid>

                <Grid size={12}>
                  <Box sx={{ color: 'error.main' }}>
                    {errors.map((error: string) => (
                      <li key={error}>{error}</li>
                    ))}
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, lg: 7 }}>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        1. Kép
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        A felső területet tölti ki. Kerüld az apró szöveget a
                        képen.
                      </Typography>
                      <Autocomplete
                        disablePortal
                        id="publisherId"
                        disabled={readonly}
                        value={values.publisherId ?? publisherId ?? null}
                        onChange={(_event, value) => {
                          setPublisherId(value);
                          setFieldValue('publisherId', value ?? null);
                          publisherLocalStorage.setLocalStorageValue(value);
                        }}
                        getOptionLabel={(option) =>
                          publishers?.find((g: any) => g.id === option)?.name ??
                          ''
                        }
                        options={publishers?.map((g: any) => g.id) ?? []}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            fullWidth
                            error={showError('publisherId')}
                            helperText={
                              showError('publisherId')
                                ? (validationErrors.publisherId as string)
                                : 'Csak új képfeltöltéshez; a slide-hoz nem mentjük.'
                            }
                            label="Publikáló (képhez)"
                            inputProps={{
                              ...params.inputProps,
                              required: false,
                            }}
                          />
                        )}
                      />
                      <Box sx={{ mt: 2 }}>
                        <ImageSelectComponent
                          image={values.image}
                          onSelect={(image: any) => {
                            setFieldValue('imageId', image.id);
                            setFieldValue('image', image);
                          }}
                          alt="kép"
                          onUploaded={(image: any) =>
                            handleUploaded(image, setFieldValue)
                          }
                          publisherId={values.publisherId ?? publisherId}
                          currentFolderId={'-1'}
                        />
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        2. Cím
                      </Typography>
                      <TextField
                        value={values.title}
                        onChange={handleChange}
                        label="Cím"
                        name="title"
                        required
                        disabled={readonly}
                        fullWidth
                        error={showError('title')}
                        helperText={
                          showError('title')
                            ? (validationErrors.title as string)
                            : `${titleLen} / ${TITLE_MAX} karakter (mobil: max. 2 sor)`
                        }
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { required: false, maxLength: TITLE_MAX },
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Sorrend
                      </Typography>
                      <TextField
                        value={values.order}
                        onChange={handleChange}
                        label="Sorrend"
                        name="order"
                        type="number"
                        disabled={readonly}
                        fullWidth
                        helperText="Kisebb = korábbi"
                      />
                    </Grid>

                    <Grid size={12}>
                      <Typography variant="subtitle2" gutterBottom>
                        3. Szöveg
                      </Typography>
                      <TextField
                        value={values.body}
                        onChange={handleChange}
                        label="Szöveg"
                        name="body"
                        disabled={readonly}
                        fullWidth
                        multiline
                        minRows={5}
                        error={showError('body')}
                        helperText={
                          showError('body')
                            ? (validationErrors.body as string)
                            : `${bodyLen} / ${BODY_MAX} karakter (mobil: görgethető)`
                        }
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { maxLength: BODY_MAX },
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 8 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        4. Gomb (CTA)
                      </Typography>
                      <TextField
                        value={values.ctaLabel}
                        onChange={handleChange}
                        label="Gomb felirata"
                        name="ctaLabel"
                        disabled={readonly}
                        fullWidth
                        placeholder="pl. Tovább"
                        error={showError('ctaLabel')}
                        helperText={
                          showError('ctaLabel')
                            ? (validationErrors.ctaLabel as string)
                            : `Üresen: „Tovább” / utolsón „Kész” · ${(values.ctaLabel ?? '').length} / ${CTA_MAX}`
                        }
                        slotProps={{
                          inputLabel: { shrink: true },
                          htmlInput: { maxLength: CTA_MAX },
                        }}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, lg: 5 }}>
                  <MobileSlidePreview
                    title={values.title}
                    body={values.body}
                    ctaLabel={values.ctaLabel}
                    image={values.image}
                  />
                </Grid>

                <Grid container sx={{ pt: 2, justifyContent: 'left' }}>
                  {!readonly && (
                    <Grid
                      size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
                      sx={{ p: 1 }}
                    >
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        fullWidth={isSmallScreen}
                        disabled={isSubmitting || uploading}
                      >
                        Mentés
                      </Button>
                    </Grid>
                  )}
                  <Grid
                    size={{ xs: 12, sm: isSmallScreen ? 12 : 'auto' }}
                    sx={{ p: 1 }}
                  >
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth={isSmallScreen}
                      onClick={handleCancel}
                    >
                      Mégse
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Paper>
          </Form>
        );
      }}
    </Formik>
  );
};

export default OnboardingSlideForm;
